import { t as tr } from "../../lib/i18n";
import { LearnImage } from "../../ui/LearnImage";
import { Icon } from "../../ui/Icon";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { Alert } from "../../components/Form";
import { Card, Progress, Screen, StudyGate, TopBar, titleCase, useLang } from "./kit";

type Lesson = {
  id: string; spCode: string; spDesc: string; done: boolean; emoji?: string; imageId?: string;
  titleMs: string; titleEn: string; summaryMs: string; summaryEn: string;
  pointsMs: string[]; pointsEn: string[]; exampleMs: string; exampleEn: string;
};
type Data = {
  unit: { id: string; titleMs: string; titleEn: string | null; emoji: string | null; versionId: string; questions: number; mastery: number | null; tp: number | null };
  lessons: Lesson[];
};

export function UnitPage() {
  return (
    <Screen>
      <StudyGate>
        <Inner />
      </StudyGate>
    </Screen>
  );
}

function Inner() {
  const { topicId = "" } = useParams();
  const { pick, lang } = useLang();
  const [data, setData] = useState<Data | null>(null);
  const [open, setOpen] = useState(0);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<Data>(`/api/learn/unit/${topicId}`)
      .then((d) => {
        setData(d);
        const firstTodo = d.lessons.findIndex((l) => !l.done);
        setOpen(firstTodo < 0 ? 0 : firstTodo);
      })
      .catch((e) => setError(errorMessage(e)));
  }, [topicId]);

  async function markDone(i: number) {
    const l = data!.lessons[i]!;
    if (!l.done) {
      await api(`/api/learn/lessons/${l.id}/complete`, { method: "POST" }).catch(() => undefined);
      setData((d) => d && { ...d, lessons: d.lessons.map((x, j) => (j === i ? { ...x, done: true } : x)) });
    }
    setOpen(i + 1);
    setTimeout(() => document.getElementById(`lesson-${i + 1}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!data) return <p className="text-sm text-slate-500">…</p>;
  const u = data.unit;
  const done = data.lessons.filter((l) => l.done).length;

  return (
    <>
      <TopBar back={`/belajar/subjek/${u.versionId}`} title={pick(titleCase(u.titleMs), u.titleEn)}
        subtitle={`${done}/${data.lessons.length} ${tr("pelajaran selesai")}${u.mastery !== null ? ` · ${tr("Kuiz terbaik")} ${u.mastery}%` : ""}`} />
      <div className="mb-4"><Progress value={done} max={data.lessons.length} /></div>

      <ol className="space-y-3">
        {data.lessons.map((l, i) => {
          const isOpen = open === i;
          return (
            <li key={l.id} id={`lesson-${i}`}>
              <Card className={isOpen ? "ring-2 ring-emerald-300" : ""}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-3 text-left" aria-expanded={isOpen}>
                  <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl bg-sky-50">{l.imageId ? <LearnImage id={l.imageId} className="h-16 w-16" /> : <Icon name="pencil" className="h-6 w-6 text-sky-500" />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400">SP {l.spCode}</span>
                    <span className="block font-extrabold text-slate-900">{pick(l.titleMs, l.titleEn)}</span>
                  </span>
                  <span className={`text-xl ${l.done ? "text-emerald-500" : "text-slate-300"}`}>{l.done ? "✔" : isOpen ? "▾" : "▸"}</span>
                </button>
                {isOpen && (
                  <div className="mt-4 space-y-4">
                    <LearnImage id={l.imageId} alt={pick(l.titleMs, l.titleEn)} className="mx-auto aspect-[4/3] w-full max-w-md" />
                    <p className="text-base leading-relaxed text-slate-700">{pick(l.summaryMs, l.summaryEn)}</p>
                    <div className="rounded-2xl bg-emerald-50 p-4">
                      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700">{tr("Nota penting")}</p>
                      <ul className="space-y-2">
                        {(lang === "en" && l.pointsEn?.length ? l.pointsEn : l.pointsMs?.length ? l.pointsMs : l.pointsEn).map((p, k) => (
                          <li key={k} className="flex gap-2 text-slate-800"><span className="text-emerald-500">●</span><span>{p}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-2xl bg-amber-50 p-4">
                      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-amber-700">{tr("Contoh")}</p>
                      <p className="text-slate-800">{pick(l.exampleMs, l.exampleEn)}</p>
                    </div>
                    <details className="text-xs text-slate-400">
                      <summary className="cursor-pointer">{tr("Standard Pembelajaran (DSKP)")}</summary>
                      <p className="mt-1">{l.spCode} {l.spDesc}</p>
                    </details>
                    <button type="button" onClick={() => markDone(i)}
                      className="w-full rounded-full bg-emerald-500 py-3 text-sm font-extrabold text-white shadow-sm active:scale-[0.99]">
                      {i + 1 < data.lessons.length ? tr("Faham! Seterusnya →") : tr("Faham! ✔")}
                    </button>
                  </div>
                )}
              </Card>
            </li>
          );
        })}
      </ol>

      {u.questions > 0 && (
        <Card className="mt-6 bg-gradient-to-br from-violet-500 to-fuchsia-500 text-center text-white ring-0">
          <Icon name="cap" className="mx-auto h-9 w-9 text-brand" />
          <p className="mt-1 text-lg font-extrabold">{tr("Sedia untuk kuiz?")}</p>
          <p className="text-sm opacity-90">{tr("10 soalan daripada unit ini. Jawapan terus disemak.")}</p>
          <Link to={`/belajar/unit/${u.id}/kuiz`} className="mt-3 inline-block rounded-full bg-white px-6 py-2 text-sm font-extrabold text-violet-600">
            {tr("Mula kuiz")}
          </Link>
        </Card>
      )}
    </>
  );
}
